/* eslint-disable no-unused-expressions */
/* eslint-disable max-classes-per-file */
import jwtManager from '@/utils/jwtManager';
import { ICloudUser } from '@/utils/trackings';
import axios from 'axios';
import moment from 'moment';
import Vue from 'vue';

export interface IChat {
  isReady: boolean;
  name: string;
  toggleSupport: boolean;
  opened: boolean;
  show(): void;
  hide(): void;
  open(): void;
  close(): void;
  user(user: ICloudUser): void;
}

function getZendeskContextId(): string {
  return 'crmsol';
}

class CrispChat implements IChat {
  chat: any;

  constructor(chat) {
    this.chat = chat;
  }

  get isReady(): boolean {
    return typeof (this.chat?.do) === 'function' && typeof (this.chat?.is) === 'function';
  }

  get name(): string {
    return 'Crisp';
  }

  get toggleSupport(): boolean {
    return false;
  }

  get opened(): boolean {
    return this.chat?.is('chat:visible');
  }

  show() {
    this.chat?.do('chat:show');
  }

  hide() {
    this.chat?.do('chat:hide');
  }

  open() {
    try {
      this.chat.do('chat:open');
    } catch { }
  }

  close() {
    this.chat?.do('chat:close');
  }

  user(user: ICloudUser) {
    if (jwtManager.refreshToken === null) {
      return;
    }

    if (!user) {
      return;
    }

    if (this.chat?.set) {
      const session = [];
      this.chat.set('user:email', user.userName);
      this.chat.set('user:nickname', user.name);
      this.chat.set('user:avatar', user.avatar);
      session.push(['status', user.trial ? 'trial' : 'payed']);
      session.push(['expiration', moment(user.expirationDate).format('L')]);
      session.push(['users', user.maxUsers]);
      session.push(['company_id', user.customerId]);
      session.push(['user_id', user.userId]);
      if (user.consumerPackageId) {
        if (user.consumerPackageId.startsWith('A-S')) {
          session.push(['licence', 'ONLINE']);
        } else if (user.consumerPackageId.startsWith('SDS-')) {
          session.push(['licence', 'SoftwareDelSol']);
        } else {
          session.push(['licence', 'OFFLINE']);
        }
      }
      session.push(['consumerPackageId', user.consumerPackageId]);
      session.push(['customerCode', user.customerCode]);
      session.push(['shard', user.shard]);
      session.push(['version', user.version]);
      if (user.score) {
        session.push(['nps', user.score]);
      }
      if (this.chat.push) {
        this.chat.push(['set', 'session:data', [session]]);
      }
    }
  }
}

class ZendeskChat implements IChat {
  private chat: any;

  private isVisible: boolean;

  constructor(chat) {
    this.chat = chat;
    this.isVisible = true;
    this.registerEvents();
    this.initializeConversationFields();
  }

  private initializeConversationFields(): void {
    const contextId = getZendeskContextId();

    if (this.chat && typeof this.chat === 'function') {
      try {
        this.chat('messenger:set', 'conversationFields', [
          {
            id: '38426873612561',
            value: contextId,
          },
        ]);

        this.chat('messenger:set', 'conversationTags', ['crmsol-platform', 'web-widget']);

        console.log(`ZendeskChat: Conversation fields set with context: ${contextId}`);
      } catch (error) {
        console.error('ZendeskChat: Error setting conversation fields:', error);
      }
    }
  }

  registerEvents(): void {
    this.chat?.(
      'messenger:on',
      'open',
      () => {
        this.isVisible = true;
      },
    );
  }

  get name(): string {
    return 'Zendesk';
  }

  get isReady(): boolean {
    return typeof this.chat === 'function';
  }

  get toggleSupport(): boolean {
    return true;
  }

  get opened(): boolean {
    return this.isVisible;
  }

  show() {
    this.ensureConversationFields();
    this.chat?.('messenger', 'show');
    // Ensure context_id is set when showing the chat
    this.setConversationFields();
  }

  private ensureConversationFields(): void {
    if (this.chat && typeof this.chat === 'function') {
      const contextId = getZendeskContextId();

      try {
        this.chat('messenger:set', 'conversationFields', [
          {
            id: '38426873612561',
            value: contextId,
          },
        ]);

        console.log(`ZendeskChat: Ensured conversation fields with context: ${contextId}`);
      } catch (error) {
        console.error('ZendeskChat: Error ensuring conversation fields:', error);
      }
    }
  }

  hide() {
    this.chat?.('messenger', 'hide');
    this.isVisible = false;
  }

  open() {
    this.show();
    this.chat?.('messenger', 'open');
  }

  close() {
    this.chat?.('messenger', 'close');
  }

  user(user: ICloudUser) {
    if (jwtManager.refreshToken === null) {
      return;
    }
    if (!user) {
      return;
    }
    this.loginUser(user);
  }

  private async requestJwtToken(): Promise<string | null> {
    try {
      const response = await axios.get('Zendesk/CreateJwtToken');
      const jwtToken = response?.data?.jwt;
      if (!jwtToken) {
        console.error('Zendesk - BE - Zendesk.CreateJwtToken - No token');
        return;
      }
      return jwtToken;
    } catch (error) {
      console.error(`Zendesk - BE - Zendesk.CreateJwtToken - Error: ${(error as Error).message}`);
    }
  }

  private setConversationFields(): void {
    const contextId = getZendeskContextId(); // Always returns 'crmsol'

    if (this.chat && typeof this.chat === 'function') {
      this.chat('messenger', 'set', 'conversationFields', [
        {
          id: '38426873612561', // Fixed ID for context_id field in Zendesk
          value: contextId, // This ensures context_id is always 'crmsol'
        },
      ]);
    }
  }

  private loginUser(_user: ICloudUser) {
    this.hide();
    const refZendesk = this.chat;
    if (!refZendesk) {
      return;
    }
    refZendesk(
      'messenger',
      'loginUser',
      (callback) => {
        // https://developer.zendesk.com/api-reference/widget-messaging/web/authentication/
        this.requestJwtToken()
          .then(async (jwtToken) => {
            if (!jwtToken) {
              return;
            }
            callback(jwtToken);
            // Context is now set globally in index.html before any user interaction
          })
          .catch((error) => {
            console.error(`Zendesk - requestJwtToken - ${(error as Error)?.message}`);
          });
      },
      (error) => {
        if (error) {
          const { type, reason, message } = error;
          console.error(`Zendesk - Error - Type: ${type} - Reason: ${reason} - ${message}`);
        }
      },
    );
  }
}

class FacadeChat implements IChat {
  private myChat: IChat = null;

  get chat(): IChat {
    if (this.myChat) {
      return this.myChat;
    }

    const w = window as any;
    let newVal: IChat = null;
    if (w.$crisp) {
      newVal = new CrispChat(w.$crisp);
    }

    if (w.zE) {
      newVal = new ZendeskChat(w.zE);
    }

    this.myChat = newVal;

    return newVal;
  }

  get isReady(): boolean {
    return this.chat?.isReady ?? false;
  }

  get toggleSupport(): boolean {
    return this.chat?.toggleSupport ?? false;
  }

  get opened(): boolean {
    try {
      return this.chat?.opened ?? false;
    } catch {
      // ignored
    }
    return false;
  }

  get name(): string {
    return this.chat?.name ?? 'None';
  }

  show() {
    try {
      this.chat?.show();
    } catch {
      // ignored
    }
  }

  hide() {
    try {
      this.chat?.hide();
    } catch {
      // ignored
    }
  }

  open() {
    try {
      this.chat?.open();
    } catch {
      // ignored
    }
  }

  close() {
    try {
      this.chat?.close();
    } catch {
      // ignored
    }
  }

  user(user: ICloudUser) {
    try {
      this.chat?.user(user);
    } catch {
      // ignored
    }
  }
}

export default () => {
  loadZendeskScriptWithFields();

  Vue.use((v) => {
    v.prototype.$chat = v.chat = new FacadeChat();
  });
};

function loadZendeskScriptWithFields(): void {
  if (document.getElementById('ze-snippet')) {
    setZendeskConversationFields();
    return;
  }

  const script = document.createElement('script');
  script.id = 'ze-snippet';
  script.async = true;

  script.onload = () => {
    setZendeskConversationFields();
  };

  script.onerror = () => {
    console.error('Failed to load Zendesk Web Widget');
  };

  document.head.appendChild(script);
}

function setZendeskConversationFields(): void {
  const waitForZE = () => {
    if (typeof (window as any).zE === 'function') {
      const zE = (window as any).zE;
      const contextId = getZendeskContextId();

      try {
        zE('messenger:set', 'conversationFields', [
          {
            id: '38426873612561',
            value: contextId,
          },
        ]);
        zE('messenger:set', 'conversationTags', ['crmsol-platform', 'web-widget']);
        zE('messenger', 'hide');
      } catch (error) {
        console.error('Error setting Zendesk conversation fields:', error);
      }
    } else {
      setTimeout(waitForZE, 50);
    }
  };

  waitForZE();
}
